import React, { useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { UserContext, UserProvider } from './context/UserContext';
import { Toaster } from 'sonner';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import ClienteManager from './components/ClienteManager';
import FacturaList from './components/FacturaList';
import ClienteForm from './components/ClienteForm';
import AbonosForm from './components/AbonosForm';
import FacturaForm from './components/FacturaForm';
import EditarFactura from './components/EditarFactura';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import UsuarioList from './components/UsuarioList';
import UsuarioForm from './components/UsuarioForm';
import HonorarioList from './components/HonorarioList';
import HonorarioForm from './components/HonorarioForm';
import AbonarHonorario from './components/AbonarHonorario';
import api from './api';
import DetalleCliente from './components/DetalleCliente';
import CrearFactura from './components/CrearFactura';
import ListadoClientes from './components/ListadoClientes';
import ListadoRenta from './components/ListadoRenta';
import ListadoArriendos from './components/ListadoArriendos';
import FactoresArriendo from './components/FactoresArriendo';
import DashboardRentas from './components/DashboardRentas';
import EstadosRenta from './components/EstadosRenta';
import MonitorScraping from './components/MonitorScraping';
import PanelContabilidad from './components/PanelContabilidad';
import PanelF29Admin from './components/PanelF29Admin';
import PanelInformesIva from './components/informes-iva/PanelInformesIva';
import InformeIvaConfigRapida from './components/informes-iva/InformeIvaConfigRapida';
import InformeIvaCorreoPage from './components/informes-iva/InformeIvaCorreoPage';
import InformeIvaOperacionesPage from './components/informes-iva/InformeIvaOperacionesPage';
import InformeIvaVerPage from './components/informes-iva/InformeIvaVerPage';
import { VacacionesDashboard } from './components/vacaciones/VacacionesDashboard';

const App = () => {
  const { currentUser, setCurrentUser, loading, setLoading } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthRoute = location.pathname === '/login' || location.pathname === '/';
  const isFullBleed = location.pathname.startsWith('/dashboard');
  const isAuthenticated = Boolean(currentUser);  // Verifica si hay un usuario autenticado

  useEffect(() => {
    if (isAuthenticated) {
      if (location.pathname === '/login' || location.pathname === '/') {
        if (currentUser?.role === 'USER') {
          navigate('/dashboard/rentas');  // Cambiar aquí también
        } else {
          navigate('/dashboard');
        }
      }
    }
  }, [isAuthenticated, location, navigate, currentUser]);

  const handleLogin = async (userData) => {
    const { token, usuario } = userData;

    if (!token || !usuario) {
      console.error('Datos incompletos al iniciar sesión');
      return;
    }

    localStorage.setItem('token', token); // Guarda el token en el localStorage

    const { role, ...otherData } = usuario;

    setCurrentUser({
      ...otherData,
      id: usuario.id, // Usar 'id' en lugar de '_id'
      role: usuario.role,
    });

    // Redirigir según rol
    if (usuario.role === 'USER') {
      navigate('/dashboard/rentas'); 
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('token');
    navigate('/login');
  };

 // Modificar la función fetchUser en useEffect
useEffect(() => {
  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    //console.log("Token en localStorage:", token ? "Existe" : "No existe");
    
    if (token) {
      try {
        // La autenticación se maneja en el interceptor de api.js
        //console.log("Intentando obtener datos del usuario...");
        const response = await api.get('/usuarios/me');
        //console.log("Respuesta recibida:", response.data);
        
        setCurrentUser({
          ...response.data,
          id: response.data.id,
        });
       // console.log("Usuario establecido en el contexto");
      } catch (error) {
        console.error('Error al obtener usuario:', error);
        
        // Solo eliminar token en caso de error 401 explícito
        if (error.response && error.response.status === 401) {
          console.warn("Error 401, eliminando token");
          localStorage.removeItem('token');
          setCurrentUser(null);
        } else {
          // Para otros errores (como de red), mantener el token
          console.warn("Error que no es 401, manteniendo token");
        }
      }
    } else {
      console.log("No hay token, usuario no autenticado");
      setCurrentUser(null);
    }
    setLoading(false);
  };

  fetchUser();
}, [setCurrentUser, setLoading]);


  if (loading) {
    // Puedes personalizar este loader
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <div>
      <Toaster position="top-right" />
      {!isAuthRoute && <Navbar user={currentUser} onLogout={handleLogout} />}

  <div className={`${isFullBleed ? 'w-full' : 'container mx-auto'} ${!isAuthRoute && !isFullBleed ? 'mt-4' : ''}`}>
        <Routes>
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/" element={<Login onLogin={handleLogin} />} />
          <Route path="/abonosHonorarios/:honorarioId" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={AbonarHonorario} />} />

          <Route path="/dashboard" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={Dashboard} />} />
          <Route path="/clientes" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={ClienteManager} />} />
          <Route path="/facturas" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={FacturaList} />} />
          <Route path="/nuevo-cliente" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={ClienteForm} />} />
          <Route path="/abonos" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={AbonosForm} />} />
          <Route path="/facturas/nueva" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={CrearFactura} />} />
          <Route path="/facturas/editar/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={EditarFactura} />} />
          <Route path="/honorarios" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={HonorarioList} />} />
          <Route path="/honorario-nuevo" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={HonorarioForm} />} />
          <Route path="/honorarios/editar/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={HonorarioForm} />} />
          <Route path="/clientes/detalle/:rut" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={DetalleCliente} />} />
          {/* Rutas de listados - accesibles para todos los roles autenticados */}
          <Route path="/listados" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={ListadoClientes} />} />
          <Route path="/listados/iva" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={ListadoClientes} />} />
          <Route path="/listados/renta" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={ListadoRenta} />} />
          <Route path="/estados-renta" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={EstadosRenta} />} />
          <Route path="/monitor-sii" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={MonitorScraping} />} />
          <Route
            path="/contabilidad"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={PanelContabilidad}
                requiredRole="ADMIN"
              />
            }
          />
          <Route
            path="/f29"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={PanelF29Admin}
                requiredRole="ADMIN"
              />
            }
          />
          <Route
            path="/informes-iva"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={PanelInformesIva}
                requiredRole={['ADMIN', 'FACTURACION']}
              />
            }
          />
          <Route
            path="/informes-iva/configuracion"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={InformeIvaConfigRapida}
                requiredRole={['ADMIN', 'FACTURACION']}
              />
            }
          />
          <Route
            path="/informes-iva/correo"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={InformeIvaCorreoPage}
                requiredRole={['ADMIN', 'FACTURACION']}
              />
            }
          />
          <Route
            path="/informes-iva/operaciones"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={InformeIvaOperacionesPage}
                requiredRole={['ADMIN', 'FACTURACION']}
              />
            }
          />
          <Route
            path="/informes-iva/ver/:runId"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={InformeIvaVerPage}
                requiredRole={['ADMIN', 'FACTURACION']}
              />
            }
          />
          <Route path="/listados/arriendo" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={ListadoArriendos} />} />
          <Route path="/factores-arriendo" element={<ProtectedRoute isAuthenticated={isAuthenticated} currentUser={currentUser} element={FactoresArriendo} />} />
          <Route path="/dashboard/rentas" element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              currentUser={currentUser}
              element={DashboardRentas}
            />} />
          {/* Rutas para usuarios */}
          <Route
            path="/usuarios"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={UsuarioList}
                requiredRole="ADMIN"
              />
            }
          />

          {/* Rutas del sistema de vacaciones - ADMIN y FACTURACION */}
          <Route
            path="/vacaciones"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={VacacionesDashboard}
                requiredRole={["ADMIN", "FACTURACION"]}
              />
            }
          />

          <Route
            path="/mi-perfil"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                element={UsuarioForm}
              />

            }
          />

          {/* Ruta 404 */}
          <Route path="*" element={<h1 className="text-center text-red-500 text-xl">Página no encontrada</h1>} />
        </Routes>
      </div>
    </div>
  );
};

export default function AppWrapper() {
  return (
    <UserProvider>
      <Router>
        <App />
      </Router>
    </UserProvider>
  );
}